import { type FC, useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/components/base';
import LocalAIAssistant from './LocalAIAssistant';

type ToolCategory =
  | 'IA geral'
  | 'Pesquisa'
  | 'Jurisprudência'
  | 'Escrita'
  | 'Resumo'
  | 'Áudio/vídeo'
  | 'Organização'
  | 'Outros';

type PromptCategory =
  | 'Petição inicial'
  | 'Contestação'
  | 'Réplica'
  | 'Recurso'
  | 'Habeas Corpus'
  | 'Defesa criminal'
  | 'Direito militar'
  | 'Contratos'
  | 'Análise de processo'
  | 'Atendimento'
  | 'Marketing jurídico'
  | 'Estudos'
  | 'Outros';

type AiTool = {
  id: string;
  name: string;
  url: string;
  category: ToolCategory;
  description: string;
  notes: string;
  isFavorite: boolean;
  createdAt: string;
  updatedAt: string;
};

type AiPrompt = {
  id: string;
  title: string;
  category: PromptCategory;
  description: string;
  content: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
};

type ToolFormState = {
  name: string;
  url: string;
  category: ToolCategory;
  description: string;
  notes: string;
  isFavorite: boolean;
};

type PromptFormState = {
  title: string;
  category: PromptCategory;
  description: string;
  content: string;
  notes: string;
};

type StoredState = {
  tools: AiTool[];
  prompts: AiPrompt[];
};

const STORAGE_KEY = 'bm-legal-os:ai-prompts-hub';

const toolCategories: ToolCategory[] = ['IA geral', 'Pesquisa', 'Jurisprudência', 'Escrita', 'Resumo', 'Áudio/vídeo', 'Organização', 'Outros'];
const promptCategories: PromptCategory[] = ['Petição inicial', 'Contestação', 'Réplica', 'Recurso', 'Habeas Corpus', 'Defesa criminal', 'Direito militar', 'Contratos', 'Análise de processo', 'Atendimento', 'Marketing jurídico', 'Estudos', 'Outros'];

const emptyToolForm: ToolFormState = {
  name: '',
  url: '',
  category: 'IA geral',
  description: '',
  notes: '',
  isFavorite: false,
};

const emptyPromptForm: PromptFormState = {
  title: '',
  category: 'Petição inicial',
  description: '',
  content: '',
  notes: '',
};

const createId = () => (globalThis.crypto && 'randomUUID' in globalThis.crypto ? globalThis.crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const normalizeUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(trimmed)) return `https://${trimmed}`;
  return '';
};

const safeText = (value: unknown) => (typeof value === 'string' ? value : '');

const loadState = (): StoredState => {
  if (typeof window === 'undefined') {
    return { tools: [], prompts: [] };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { tools: [], prompts: [] };

    const parsed = JSON.parse(raw) as Partial<StoredState>;
    const tools = Array.isArray(parsed.tools)
      ? parsed.tools.map((tool) => ({
          id: safeText(tool.id) || createId(),
          name: safeText(tool.name),
          url: safeText(tool.url),
          category: toolCategories.includes(tool.category as ToolCategory) ? (tool.category as ToolCategory) : 'IA geral',
          description: safeText(tool.description),
          notes: safeText(tool.notes),
          isFavorite: Boolean(tool.isFavorite),
          createdAt: safeText(tool.createdAt) || new Date().toISOString(),
          updatedAt: safeText(tool.updatedAt) || new Date().toISOString(),
        }))
      : [];
    const prompts = Array.isArray(parsed.prompts)
      ? parsed.prompts.map((prompt) => ({
          id: safeText(prompt.id) || createId(),
          title: safeText(prompt.title),
          category: promptCategories.includes(prompt.category as PromptCategory) ? (prompt.category as PromptCategory) : 'Petição inicial',
          description: safeText(prompt.description),
          content: safeText(prompt.content),
          notes: safeText(prompt.notes),
          createdAt: safeText(prompt.createdAt) || new Date().toISOString(),
          updatedAt: safeText(prompt.updatedAt) || new Date().toISOString(),
        }))
      : [];

    return { tools, prompts };
  } catch {
    return { tools: [], prompts: [] };
  }
};

const AIPage: FC = () => {
  const { addToast } = useToast();
  const [tools, setTools] = useState<AiTool[]>([]);
  const [prompts, setPrompts] = useState<AiPrompt[]>([]);
  const [toolSearch, setToolSearch] = useState('');
  const [promptSearch, setPromptSearch] = useState('');
  const [toolForm, setToolForm] = useState<ToolFormState>(emptyToolForm);
  const [promptForm, setPromptForm] = useState<PromptFormState>(emptyPromptForm);
  const [editingToolId, setEditingToolId] = useState<string | null>(null);
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [isToolFormOpen, setIsToolFormOpen] = useState(false);
  const [isPromptFormOpen, setIsPromptFormOpen] = useState(false);
  const [toolStatus, setToolStatus] = useState<'idle' | 'saving'>('idle');
  const [promptStatus, setPromptStatus] = useState<'idle' | 'saving'>('idle');

  useEffect(() => {
    const stored = loadState();
    setTools(stored.tools);
    setPrompts(stored.prompts);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ tools, prompts }));
  }, [tools, prompts]);

  const filteredTools = useMemo(() => {
    const query = toolSearch.trim().toLowerCase();
    if (!query) return tools;
    return tools.filter((tool) =>
      [tool.name, tool.category, tool.description, tool.notes, tool.url]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(query)),
    );
  }, [toolSearch, tools]);

  const filteredPrompts = useMemo(() => {
    const query = promptSearch.trim().toLowerCase();
    if (!query) return prompts;
    return prompts.filter((prompt) =>
      [prompt.title, prompt.category, prompt.description, prompt.content, prompt.notes]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(query)),
    );
  }, [promptSearch, prompts]);

  const resetToolForm = () => {
    setToolForm(emptyToolForm);
    setEditingToolId(null);
    setIsToolFormOpen(false);
  };

  const resetPromptForm = () => {
    setPromptForm(emptyPromptForm);
    setEditingPromptId(null);
    setIsPromptFormOpen(false);
  };

  const saveTool = () => {
    const name = toolForm.name.trim();
    const normalizedUrl = normalizeUrl(toolForm.url);

    if (!name) {
      addToast('Informe o nome da ferramenta.', 'error');
      return;
    }

    if (!normalizedUrl) {
      addToast('Informe uma URL válida para abrir a ferramenta em nova aba.', 'error');
      return;
    }

    setToolStatus('saving');

    const now = new Date().toISOString();
    const nextTool: AiTool = {
      id: editingToolId ?? createId(),
      name,
      url: normalizedUrl,
      category: toolForm.category,
      description: toolForm.description.trim(),
      notes: toolForm.notes.trim(),
      isFavorite: toolForm.isFavorite,
      createdAt: tools.find((tool) => tool.id === editingToolId)?.createdAt ?? now,
      updatedAt: now,
    };

    setTools((current) => {
      const exists = current.some((tool) => tool.id === nextTool.id);
      return exists ? current.map((tool) => (tool.id === nextTool.id ? nextTool : tool)) : [nextTool, ...current];
    });

    addToast(editingToolId ? 'Ferramenta atualizada.' : 'Ferramenta cadastrada.', 'success');
    setToolStatus('idle');
    resetToolForm();
  };

  const savePrompt = () => {
    const title = promptForm.title.trim();
    const content = promptForm.content.trim();

    if (!title) {
      addToast('Informe o título do prompt.', 'error');
      return;
    }

    if (!content) {
      addToast('Informe o conteúdo do prompt.', 'error');
      return;
    }

    setPromptStatus('saving');

    const now = new Date().toISOString();
    const nextPrompt: AiPrompt = {
      id: editingPromptId ?? createId(),
      title,
      category: promptForm.category,
      description: promptForm.description.trim(),
      content,
      notes: promptForm.notes.trim(),
      createdAt: prompts.find((prompt) => prompt.id === editingPromptId)?.createdAt ?? now,
      updatedAt: now,
    };

    setPrompts((current) => {
      const exists = current.some((prompt) => prompt.id === nextPrompt.id);
      return exists ? current.map((prompt) => (prompt.id === nextPrompt.id ? nextPrompt : prompt)) : [nextPrompt, ...current];
    });

    addToast(editingPromptId ? 'Prompt atualizado.' : 'Prompt cadastrado.', 'success');
    setPromptStatus('idle');
    resetPromptForm();
  };

  const editTool = (tool: AiTool) => {
    setEditingToolId(tool.id);
    setToolForm({
      name: tool.name,
      url: tool.url,
      category: tool.category,
      description: tool.description,
      notes: tool.notes,
      isFavorite: tool.isFavorite,
    });
    setIsToolFormOpen(true);
  };

  const editPrompt = (prompt: AiPrompt) => {
    setEditingPromptId(prompt.id);
    setPromptForm({
      title: prompt.title,
      category: prompt.category,
      description: prompt.description,
      content: prompt.content,
      notes: prompt.notes,
    });
    setIsPromptFormOpen(true);
  };

  const deleteTool = (id: string) => {
    setTools((current) => current.filter((tool) => tool.id !== id));
    addToast('Ferramenta removida.', 'success');
  };

  const deletePrompt = (id: string) => {
    setPrompts((current) => current.filter((prompt) => prompt.id !== id));
    addToast('Prompt removido.', 'success');
  };

  const toggleFavorite = (id: string) => {
    setTools((current) =>
      current.map((tool) =>
        tool.id === id
          ? {
              ...tool,
              isFavorite: !tool.isFavorite,
              updatedAt: new Date().toISOString(),
            }
          : tool,
      ),
    );
  };

  const openTool = (url: string) => {
    const targetUrl = normalizeUrl(url);
    if (!targetUrl) {
      addToast('A URL da ferramenta está inválida.', 'error');
      return;
    }

    const popup = window.open(targetUrl, '_blank', 'noopener,noreferrer');
    if (popup) {
      popup.opener = null;
    } else {
      addToast('Não foi possível abrir a ferramenta. Verifique o bloqueio do navegador.', 'error');
    }
  };

  const copyPrompt = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      addToast('Prompt copiado.', 'success');
    } catch {
      addToast('Não foi possível copiar o prompt.', 'error');
    }
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-6">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-foreground-900">IA e Prompts</h2>
          <p className="text-sm text-foreground-500 max-w-3xl">
            Centralize ferramentas externas de IA e prompts jurídicos reutilizáveis. O BM Legal OS não acessa contas externas; cada ferramenta abre em nova aba com o login do próprio usuário.
          </p>
          <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm">
            Antes de enviar informações sigilosas a ferramentas externas de IA, revise termos de uso e políticas de privacidade. Sempre que possível, anonimize dados sensíveis.
          </div>
        </div>

        <LocalAIAssistant />

        <section className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-foreground-900">Ferramentas de IA</h3>
              <p className="text-sm text-foreground-500">Cadastre links úteis como ChatGPT, Claude, Gemini, Perplexity, NotebookLM ou Copilot.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setIsToolFormOpen((current) => !current);
                if (isToolFormOpen) resetToolForm();
              }}
              className="px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600"
            >
              {isToolFormOpen ? 'Fechar cadastro' : 'Nova ferramenta'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs font-medium text-foreground-600">Buscar ferramenta</span>
              <input value={toolSearch} onChange={(event) => setToolSearch(event.target.value)} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm" placeholder="Pesquisar por nome, categoria, descrição ou URL" />
            </label>
          </div>

          {isToolFormOpen && (
            <div className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-foreground-600">Nome da ferramenta</span>
                  <input value={toolForm.name} onChange={(event) => setToolForm((current) => ({ ...current, name: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm" placeholder="ChatGPT" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-foreground-600">URL</span>
                  <input value={toolForm.url} onChange={(event) => setToolForm((current) => ({ ...current, url: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm" placeholder="https://chat.openai.com" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-foreground-600">Categoria</span>
                  <select value={toolForm.category} onChange={(event) => setToolForm((current) => ({ ...current, category: event.target.value as ToolCategory }))} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm">
                    {toolCategories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-foreground-600">Observações</span>
                  <input value={toolForm.notes} onChange={(event) => setToolForm((current) => ({ ...current, notes: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm" placeholder="Use para revisão de peças e pesquisa" />
                </label>
              </div>
              <label className="space-y-1 block">
                <span className="text-xs font-medium text-foreground-600">Finalidade / descrição</span>
                <textarea value={toolForm.description} onChange={(event) => setToolForm((current) => ({ ...current, description: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm min-h-24" placeholder="Ferramenta externa para pesquisa e apoio jurídico." />
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-foreground-700">
                <input type="checkbox" checked={toolForm.isFavorite} onChange={(event) => setToolForm((current) => ({ ...current, isFavorite: event.target.checked }))} />
                Marcar como favorita
              </label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={saveTool} disabled={toolStatus === 'saving'} className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60">
                  {editingToolId ? 'Salvar alterações' : 'Cadastrar ferramenta'}
                </button>
                <button type="button" onClick={resetToolForm} className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-100">
                  Limpar
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredTools.length === 0 ? (
              <div className="lg:col-span-2 xl:col-span-3 p-6 rounded-2xl border border-dashed border-background-200 bg-background-50 text-sm text-foreground-500">
                Nenhuma ferramenta de IA cadastrada. Cadastre ChatGPT, Claude, Gemini, Perplexity ou outra ferramenta que você usa.
              </div>
            ) : (
              filteredTools.map((tool) => (
                <article key={tool.id} className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-base font-semibold text-foreground-900">{tool.name}</h4>
                        {tool.isFavorite && <span className="text-[11px] px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">Favorita</span>}
                      </div>
                      <p className="text-xs text-foreground-500">{tool.category}</p>
                    </div>
                    <button type="button" onClick={() => toggleFavorite(tool.id)} className="text-xs font-medium text-primary-600 hover:text-primary-700">
                      {tool.isFavorite ? 'Desfavoritar' : 'Favoritar'}
                    </button>
                  </div>
                  <p className="text-sm text-foreground-600">{tool.description || 'Ferramenta externa cadastrada para uso interno.'}</p>
                  {tool.notes && <p className="text-xs text-foreground-500">Observações: {tool.notes}</p>}
                  <p className="text-xs text-foreground-500 break-all">{tool.url}</p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => openTool(tool.url)} className="px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600">
                      Abrir ferramenta
                    </button>
                    <button type="button" onClick={() => editTool(tool)} className="px-3 py-2 rounded-lg border border-background-200 text-xs font-medium text-foreground-700 hover:bg-background-100">
                      Editar
                    </button>
                    <button type="button" onClick={() => deleteTool(tool.id)} className="px-3 py-2 rounded-lg border border-red-200 text-xs font-medium text-red-700 hover:bg-red-50">
                      Excluir
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-foreground-900">Prompts Jurídicos</h3>
              <p className="text-sm text-foreground-500">Crie prompts reutilizáveis para petições, recursos, contratos, atendimento, estudos e outras rotinas.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setIsPromptFormOpen((current) => !current);
                if (isPromptFormOpen) resetPromptForm();
              }}
              className="px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600"
            >
              {isPromptFormOpen ? 'Fechar cadastro' : 'Novo prompt'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs font-medium text-foreground-600">Buscar prompt</span>
              <input value={promptSearch} onChange={(event) => setPromptSearch(event.target.value)} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm" placeholder="Pesquisar por título, categoria, descrição, conteúdo ou observações" />
            </label>
          </div>

          {isPromptFormOpen && (
            <div className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-foreground-600">Título</span>
                  <input value={promptForm.title} onChange={(event) => setPromptForm((current) => ({ ...current, title: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm" placeholder="Prompt para análise de processo" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-foreground-600">Categoria</span>
                  <select value={promptForm.category} onChange={(event) => setPromptForm((current) => ({ ...current, category: event.target.value as PromptCategory }))} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm">
                    {promptCategories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs font-medium text-foreground-600">Descrição / finalidade</span>
                  <input value={promptForm.description} onChange={(event) => setPromptForm((current) => ({ ...current, description: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm" placeholder="Use para sumarizar o caso e apontar próximos passos." />
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs font-medium text-foreground-600">Conteúdo do prompt</span>
                  <textarea value={promptForm.content} onChange={(event) => setPromptForm((current) => ({ ...current, content: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm min-h-32" placeholder="Escreva o prompt aqui..." />
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs font-medium text-foreground-600">Observações</span>
                  <input value={promptForm.notes} onChange={(event) => setPromptForm((current) => ({ ...current, notes: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm" placeholder="Ex.: revisar dados sigilosos antes do uso." />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={savePrompt} disabled={promptStatus === 'saving'} className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60">
                  {editingPromptId ? 'Salvar alterações' : 'Cadastrar prompt'}
                </button>
                <button type="button" onClick={resetPromptForm} className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-100">
                  Limpar
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredPrompts.length === 0 ? (
              <div className="lg:col-span-2 p-6 rounded-2xl border border-dashed border-background-200 bg-background-50 text-sm text-foreground-500">
                Nenhum prompt jurídico cadastrado. Crie prompts para petições, recursos, contratos, atendimento e estudos.
              </div>
            ) : (
              filteredPrompts.map((prompt) => (
                <article key={prompt.id} className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="text-base font-semibold text-foreground-900">{prompt.title}</h4>
                      <p className="text-xs text-foreground-500">{prompt.category}</p>
                    </div>
                    <button type="button" onClick={() => copyPrompt(prompt.content)} className="text-xs font-medium text-primary-600 hover:text-primary-700">
                      Copiar prompt
                    </button>
                  </div>
                  <p className="text-sm text-foreground-600">{prompt.description || 'Prompt jurídico reutilizável.'}</p>
                  <pre className="text-xs whitespace-pre-wrap rounded-xl bg-background-100 p-3 text-foreground-700 border border-background-200 overflow-x-auto">{prompt.content}</pre>
                  {prompt.notes && <p className="text-xs text-foreground-500">Observações: {prompt.notes}</p>}
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => editPrompt(prompt)} className="px-3 py-2 rounded-lg border border-background-200 text-xs font-medium text-foreground-700 hover:bg-background-100">
                      Editar
                    </button>
                    <button type="button" onClick={() => deletePrompt(prompt.id)} className="px-3 py-2 rounded-lg border border-red-200 text-xs font-medium text-red-700 hover:bg-red-50">
                      Excluir
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      </div>
    </AppLayout>
  );
};

export default AIPage;
